import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  Building2,
  TrendingUp,
  Calculator,
  DollarSign,
  Users,
  Clock,
  Target,
  ChevronRight,
  Play,
  Save,
  Trash2,
  Eye,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar
} from 'recharts';

export default function ExpansaoDashboard() {
  const [loading, setLoading] = useState(false);
  const [simulando, setSimulando] = useState(false);
  const [benchmark, setBenchmark] = useState(null);
  const [simulacoes, setSimulacoes] = useState([]);
  const [resultadoAtual, setResultadoAtual] = useState(null);
  const [cenarios, setCenarios] = useState(null);
  const [dialogSimulacao, setDialogSimulacao] = useState(false);
  const [dialogResultado, setDialogResultado] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    nome_unidade: 'Nova Unidade DNA',
    alunos: {
      capacidade_total: 300,
      ocupacao_inicial_percentual: 30,
      crescimento_mensal_percentual: 5
    },
    receita: {
      mensalidade_media: 1500,
      inadimplencia_percentual: 8,
      receitas_extras_percentual: 15
    },
    custos_fixos: {
      aluguel: 25000,
      folha_administrativa: 15000,
      folha_pedagogica: 40000,
      encargos: 20000,
      marketing: 5000,
      tecnologia: 3000,
      manutencao: 5000
    },
    custos_variaveis: {
      custo_por_aluno: 150,
      material_didatico: 50,
      alimentacao: 0
    },
    investimento: {
      reforma: 100000,
      mobiliario: 80000,
      tecnologia: 50000,
      marketing_inicial: 30000
    },
    meses_projecao: 36
  });

  useEffect(() => {
    loadBenchmark();
    loadSimulacoes();
  }, []);

  const loadBenchmark = async () => {
    try {
      const res = await api.get('/expansao/benchmark');
      setBenchmark(res.data);
    } catch (error) {
      console.error('Erro ao carregar benchmark:', error);
    }
  };

  const loadSimulacoes = async () => {
    try {
      const res = await api.get('/expansao/simulacoes');
      setSimulacoes(res.data.simulacoes || []);
    } catch (error) {
      console.error('Erro ao carregar simulações:', error);
    }
  };

  const handleSimular = async () => {
    setSimulando(true);
    try {
      const res = await api.post('/expansao/simular', form);
      setResultadoAtual(res.data);
      setDialogResultado(true);
      toast.success('Simulação concluída!');
      loadSimulacoes();
    } catch (error) {
      toast.error('Erro ao simular: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSimulando(false);
    }
  };

  const handleSimularCenarios = async () => {
    setSimulando(true);
    try {
      const res = await api.post('/expansao/simular-cenarios', form);
      setCenarios(res.data);
      toast.success('Cenários simulados!');
    } catch (error) {
      toast.error('Erro ao simular cenários');
    } finally {
      setSimulando(false);
    }
  };

  const handleDeleteSimulacao = async (id) => {
    if (!confirm('Excluir esta simulação?')) return;
    try {
      await api.delete(`/expansao/simulacoes/${id}`);
      toast.success('Simulação excluída');
      loadSimulacoes();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleViewSimulacao = async (id) => {
    try {
      const res = await api.get(`/expansao/simulacoes/${id}`);
      setResultadoAtual(res.data);
      setDialogResultado(true);
    } catch (error) {
      toast.error('Erro ao carregar simulação');
    }
  };

  const investimentoTotal = Object.values(form.investimento).reduce((a, b) => a + b, 0);
  const custoFixoMensal = Object.values(form.custos_fixos).reduce((a, b) => a + b, 0);

  const getViabilidadeColor = (nivel) => {
    switch (nivel) {
      case 'ALTA': return 'bg-green-500';
      case 'MÉDIA': return 'bg-yellow-500';
      case 'BAIXA': return 'bg-orange-500';
      case 'INVIÁVEL': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Building2 className="w-8 h-8 text-purple-600" />
            Simulação de Expansão
          </h1>
          <p className="text-muted-foreground mt-1">
            Projete a viabilidade de novas unidades escolares
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleSimularCenarios}
            disabled={simulando}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Comparar Cenários
          </Button>
          <Button 
            onClick={handleSimular}
            disabled={simulando}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {simulando ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Simular
          </Button>
        </div>
      </div>

      {/* Benchmark da Unidade Atual */}
      {benchmark && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Info className="w-5 h-5 text-purple-600" />
              Benchmark da Unidade Atual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Alunos Ativos</p>
                <p className="text-xl font-bold">{benchmark.unidade_atual.total_alunos}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ticket Médio</p>
                <p className="text-xl font-bold">
                  {benchmark.unidade_atual.ticket_medio_mensal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Inadimplência</p>
                <p className="text-xl font-bold">{benchmark.unidade_atual.inadimplencia_percentual.toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Custo/Mês</p>
                <p className="text-xl font-bold">
                  {benchmark.unidade_atual.custo_total_mensal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Custo/Aluno</p>
                <p className="text-xl font-bold">
                  {benchmark.unidade_atual.custo_por_aluno.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Formulário de Simulação */}
      <Tabs defaultValue="alunos" className="space-y-4">
        <TabsList className="grid grid-cols-2 md:grid-cols-5 gap-1">
          <TabsTrigger value="alunos" className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span className="hidden md:inline">Alunos</span>
          </TabsTrigger>
          <TabsTrigger value="receita" className="flex items-center gap-1">
            <DollarSign className="w-4 h-4" />
            <span className="hidden md:inline">Receita</span>
          </TabsTrigger>
          <TabsTrigger value="custos" className="flex items-center gap-1">
            <Calculator className="w-4 h-4" />
            <span className="hidden md:inline">Custos</span>
          </TabsTrigger>
          <TabsTrigger value="investimento" className="flex items-center gap-1">
            <Building2 className="w-4 h-4" />
            <span className="hidden md:inline">Investimento</span>
          </TabsTrigger>
          <TabsTrigger value="projecao" className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4" />
            <span className="hidden md:inline">Projeção</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="alunos">
          <Card>
            <CardHeader>
              <CardTitle>Configuração de Alunos</CardTitle>
              <CardDescription>Defina a capacidade e crescimento esperado</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Nome da Unidade</Label>
                  <Input 
                    value={form.nome_unidade}
                    onChange={(e) => setForm({...form, nome_unidade: e.target.value})}
                    placeholder="Ex: DNA Unidade Zona Sul"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Capacidade Total de Alunos</Label>
                  <Input 
                    type="number"
                    value={form.alunos.capacidade_total}
                    onChange={(e) => setForm({
                      ...form, 
                      alunos: {...form.alunos, capacidade_total: parseInt(e.target.value) || 0}
                    })}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Ocupação Inicial: {form.alunos.ocupacao_inicial_percentual}%</Label>
                  <Slider
                    value={[form.alunos.ocupacao_inicial_percentual]}
                    onValueChange={(v) => setForm({
                      ...form,
                      alunos: {...form.alunos, ocupacao_inicial_percentual: v[0]}
                    })}
                    max={100}
                    step={5}
                    className="mt-2"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Início com {Math.round(form.alunos.capacidade_total * form.alunos.ocupacao_inicial_percentual / 100)} alunos
                  </p>
                </div>

                <div>
                  <Label>Crescimento Mensal: {form.alunos.crescimento_mensal_percentual}%</Label>
                  <Slider
                    value={[form.alunos.crescimento_mensal_percentual]}
                    onValueChange={(v) => setForm({
                      ...form,
                      alunos: {...form.alunos, crescimento_mensal_percentual: v[0]}
                    })}
                    max={20}
                    step={1}
                    className="mt-2"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="receita">
          <Card>
            <CardHeader>
              <CardTitle>Configuração de Receita</CardTitle>
              <CardDescription>Defina mensalidades e expectativa de inadimplência</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Mensalidade Média (R$)</Label>
                  <Input 
                    type="number"
                    value={form.receita.mensalidade_media}
                    onChange={(e) => setForm({
                      ...form, 
                      receita: {...form.receita, mensalidade_media: parseFloat(e.target.value) || 0}
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Inadimplência Esperada (%)</Label>
                  <Input 
                    type="number"
                    value={form.receita.inadimplencia_percentual}
                    onChange={(e) => setForm({
                      ...form, 
                      receita: {...form.receita, inadimplencia_percentual: parseFloat(e.target.value) || 0}
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Receitas Extras (%)</Label>
                  <Input 
                    type="number"
                    value={form.receita.receitas_extras_percentual}
                    onChange={(e) => setForm({
                      ...form, 
                      receita: {...form.receita, receitas_extras_percentual: parseFloat(e.target.value) || 0}
                    })}
                  />
                  <p className="text-xs text-muted-foreground">Material, uniforme, eventos, etc.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="custos">
          <Card>
            <CardHeader>
              <CardTitle>Custos Fixos e Variáveis</CardTitle>
              <CardDescription>Total Fixo Mensal: {custoFixoMensal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium">Custos Fixos Mensais</h4>
                  {Object.entries(form.custos_fixos).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2">
                      <Label className="w-40 capitalize">{key.replace(/_/g, ' ')}</Label>
                      <Input 
                        type="number"
                        value={value}
                        onChange={(e) => setForm({
                          ...form, 
                          custos_fixos: {...form.custos_fixos, [key]: parseFloat(e.target.value) || 0}
                        })}
                        className="w-32"
                      />
                    </div>
                  ))}
                </div>
                <div className="space-y-4">
                  <h4 className="font-medium">Custos Variáveis (por Aluno)</h4>
                  {Object.entries(form.custos_variaveis).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2">
                      <Label className="w-40 capitalize">{key.replace(/_/g, ' ')}</Label>
                      <Input 
                        type="number"
                        value={value}
                        onChange={(e) => setForm({
                          ...form, 
                          custos_variaveis: {...form.custos_variaveis, [key]: parseFloat(e.target.value) || 0}
                        })}
                        className="w-32"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="investimento">
          <Card>
            <CardHeader>
              <CardTitle>Investimento Inicial</CardTitle>
              <CardDescription>Total: {investimentoTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {Object.entries(form.investimento).map(([key, value]) => (
                  <div key={key} className="space-y-2">
                    <Label className="capitalize">{key.replace(/_/g, ' ')}</Label>
                    <Input 
                      type="number"
                      value={value}
                      onChange={(e) => setForm({
                        ...form, 
                        investimento: {...form.investimento, [key]: parseFloat(e.target.value) || 0}
                      })}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="projecao">
          <Card>
            <CardHeader>
              <CardTitle>Período de Projeção</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Meses de Projeção: {form.meses_projecao}</Label>
                <Slider
                  value={[form.meses_projecao]}
                  onValueChange={(v) => setForm({...form, meses_projecao: v[0]})}
                  min={12}
                  max={60}
                  step={6}
                  className="mt-2"
                />
                <p className="text-sm text-muted-foreground">
                  {Math.floor(form.meses_projecao / 12)} anos e {form.meses_projecao % 12} meses
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Comparação de Cenários */}
      {cenarios && (
        <Card>
          <CardHeader>
            <CardTitle>Comparação de Cenários</CardTitle>
            <CardDescription>
              Investimento Total: {cenarios.investimento_total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {Object.entries(cenarios.cenarios).map(([key, cenario]) => (
                <Card key={key} className={`border-2 ${
                  cenario.resultados.viabilidade.nivel === 'ALTA' ? 'border-green-300' :
                  cenario.resultados.viabilidade.nivel === 'MÉDIA' ? 'border-yellow-300' :
                  cenario.resultados.viabilidade.nivel === 'BAIXA' ? 'border-orange-300' :
                  'border-red-300'
                }`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{cenario.nome}</CardTitle>
                    <Badge className={getViabilidadeColor(cenario.resultados.viabilidade.nivel)}>
                      {cenario.resultados.viabilidade.nivel}
                    </Badge>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Ocupação Inicial</span>
                      <span>{cenario.parametros.ocupacao_inicial.toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Crescimento/Mês</span>
                      <span>{cenario.parametros.crescimento_mensal.toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Inadimplência</span>
                      <span>{cenario.parametros.inadimplencia.toFixed(1)}%</span>
                    </div>
                    <hr />
                    <div className="flex justify-between font-medium">
                      <span>Lucro 36 Meses</span>
                      <span className={cenario.resultados.lucro_total_36m >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {cenario.resultados.lucro_total_36m.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>Payback</span>
                      <span>{cenario.resultados.payback_meses} meses</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>ROI 36m</span>
                      <span className={cenario.resultados.roi_36m >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {cenario.resultados.roi_36m.toFixed(1)}%
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Simulações Salvas */}
      <Card>
        <CardHeader>
          <CardTitle>Simulações Salvas</CardTitle>
          <CardDescription>{simulacoes.length} simulações encontradas</CardDescription>
        </CardHeader>
        <CardContent>
          {simulacoes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>Nenhuma simulação salva</p>
              <p className="text-sm">Clique em "Simular" para criar sua primeira projeção</p>
            </div>
          ) : (
            <div className="space-y-2">
              {simulacoes.map((sim) => (
                <div 
                  key={sim.id} 
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted"
                >
                  <div className="flex items-center gap-3">
                    <Badge className={getViabilidadeColor(sim.resumo.viabilidade.nivel)}>
                      {sim.resumo.viabilidade.nivel}
                    </Badge>
                    <div>
                      <p className="font-medium">{sim.nome_unidade}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(sim.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })} • 
                        Payback: {sim.resumo.payback_meses} meses • 
                        ROI: {((sim.resumo.lucro_ano1 + sim.resumo.lucro_ano2 + sim.resumo.lucro_ano3) / sim.resumo.investimento_total * 100).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleViewSimulacao(sim.id)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteSimulacao(sim.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Resultado */}
      <Dialog open={dialogResultado} onOpenChange={setDialogResultado}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {resultadoAtual?.nome_unidade}
            </DialogTitle>
          </DialogHeader>
          
          {resultadoAtual && (
            <div className="space-y-6">
              {/* Resumo */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-purple-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">Investimento</p>
                    <p className="text-lg font-bold">
                      {resultadoAtual.resumo.investimento_total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">Payback</p>
                    <p className="text-lg font-bold">{resultadoAtual.resumo.payback_meses} meses</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">Break-even</p>
                    <p className="text-lg font-bold">{resultadoAtual.resumo.break_even_alunos} alunos</p>
                  </CardContent>
                </Card>
                <Card className={`${getViabilidadeColor(resultadoAtual.resumo.viabilidade.nivel).replace('bg-', 'bg-').replace('-500', '-50')}`}>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">Viabilidade</p>
                    <Badge className={getViabilidadeColor(resultadoAtual.resumo.viabilidade.nivel)}>
                      {resultadoAtual.resumo.viabilidade.nivel}
                    </Badge>
                  </CardContent>
                </Card>
              </div>

              {/* Lucros por Ano */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">Lucro Ano 1</p>
                    <p className={`text-lg font-bold ${resultadoAtual.resumo.lucro_ano1 >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {resultadoAtual.resumo.lucro_ano1.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">Lucro Ano 2</p>
                    <p className={`text-lg font-bold ${resultadoAtual.resumo.lucro_ano2 >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {resultadoAtual.resumo.lucro_ano2.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">Lucro Ano 3</p>
                    <p className={`text-lg font-bold ${resultadoAtual.resumo.lucro_ano3 >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {resultadoAtual.resumo.lucro_ano3.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Gráfico de Evolução */}
              {resultadoAtual.projecao_mensal && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Projeção de Lucro Acumulado</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={resultadoAtual.projecao_mensal}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="mes" />
                        <YAxis tickFormatter={(v) => `R$ ${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          formatter={(v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          labelFormatter={(l) => `Mês ${l}`}
                        />
                        <Legend />
                        <Area 
                          type="monotone" 
                          dataKey="lucro_acumulado" 
                          name="Lucro Acumulado"
                          stroke="#8E52AB" 
                          fill="#c4b5fd" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Gráfico Alunos vs Receita */}
              {resultadoAtual.projecao_mensal && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Evolução de Alunos e Receita</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={resultadoAtual.projecao_mensal}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="mes" />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `R$ ${(v/1000).toFixed(0)}k`} />
                        <Tooltip />
                        <Legend />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="alunos" 
                          name="Alunos"
                          stroke="#3b82f6" 
                          strokeWidth={2}
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="receita_total" 
                          name="Receita Total"
                          stroke="#10b981" 
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
